/**
 * zapReason.js
 *
 * Why the workspace was zapped.
 *
 * `Workspace.zap()` publishes `zapAll`, and a dozen panels read that event as
 * "the workspace is gone, drop what you were showing". That is right when the
 * user discarded their work, and wrong when the zap is one step inside
 * `reconnectAndRestoreSession`, which zaps only so it can restore the very
 * state it just captured. A panel that cannot tell the two apart has to throw
 * away work that is about to come back.
 *
 * So the reason travels on the event. Panels that do not care keep ignoring the
 * payload; a panel holding something a reconnect will restore can opt out.
 *
 * A leaf module on purpose: both the publisher (`model/Workspace`) and the
 * subscribers import it, and it must not pull either of them in.
 */

/** @typedef {'user'|'reconnect'} ZapReasonValue */

export const ZapReason = {
    /**
     * The workspace was discarded on the user's behalf: File > Zap, a session
     * file being loaded over the current one, a fresh structure replacing it.
     * Whatever a panel is showing is genuinely stale.
     */
    User: 'user',
    /**
     * The workspace is being torn down so it can be rebuilt from a state
     * capture taken moments earlier. Same work, same session, new connection.
     */
    Reconnect: 'reconnect',
};

/**
 * The reason carried by a `zapAll` event. Defaults to `User`, so a publisher
 * that says nothing still means "the user discarded this".
 *
 * @param {{reason?: ZapReasonValue}} [zapEventData] the `zapAll` payload
 * @returns {ZapReasonValue}
 */
export function zapReasonOf(zapEventData) {
    return zapEventData?.reason || ZapReason.User;
}

/**
 * Is this zap part of a reconnect that is about to restore the same work?
 *
 * @param {{reason?: ZapReasonValue}} [zapEventData] the `zapAll` payload
 * @returns {boolean}
 */
export function isReconnectZap(zapEventData) {
    return zapReasonOf(zapEventData) === ZapReason.Reconnect;
}
